/** Chat glyphs — hand-drawn inline SVGs to the app's Glyph spec
 *  (24-unit viewBox, stroke 1.8, round caps/joins). No icon library.
 *  Sized via the `size` prop; colour inherits `currentColor`. */
import type { SVGProps } from "react";

function Glyph({
  size = 18,
  children,
  ...rest
}: { size?: number; children: React.ReactNode } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

type IconProps = { size?: number } & SVGProps<SVGSVGElement>;

export const ChatIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M21 11.5a8.5 8.5 0 0 1-12.2 7.7L3 21l1.9-5.6A8.5 8.5 0 1 1 21 11.5Z" />
  </Glyph>
);

export const CloseIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Glyph>
);

export const BackIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M15 18l-6-6 6-6" />
  </Glyph>
);

export const SendIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 12 20 4l-6 16-2.5-6.5L4 12Z" />
  </Glyph>
);

export const PaperclipIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8-8a3.3 3.3 0 0 1 4.7 4.7l-8 8a1.7 1.7 0 0 1-2.4-2.4l7.3-7.3" />
  </Glyph>
);

export const SmileIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    <path d="M9 9.5h.01M15 9.5h.01" />
  </Glyph>
);

export const GifIcon = (p: IconProps) => (
  <Glyph {...p}>
    <rect x="3" y="6" width="18" height="12" rx="2.5" />
    <path d="M10 10.5a2 2 0 1 0 0 3h.8V12M13.5 9.8v4.4M17.6 9.8h-2.2v4.4M15.4 12h1.6" />
  </Glyph>
);

export const SearchIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.2-3.2" />
  </Glyph>
);

export const PlusIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M12 5v14M5 12h14" />
  </Glyph>
);

export const PinIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M9 4h6l-1 5 3 3v2H7v-2l3-3-1-5Z" />
    <path d="M12 17v3" />
  </Glyph>
);

export const BellOffIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M8.7 5.2A5 5 0 0 1 17 9c0 3 .8 4.5 1.6 5.4M17.5 17H5s2-1.5 2-8v-.5" />
    <path d="M10.3 21a2 2 0 0 0 3.4 0" />
    <path d="m3 3 18 18" />
  </Glyph>
);

export const UsersIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.2a3.2 3.2 0 0 1 0 6M17.5 19a5.5 5.5 0 0 0-2-4.3" />
  </Glyph>
);

export const ExpandIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M9 4H4v5M20 15v5h-5M4 4l6 6M20 20l-6-6" />
  </Glyph>
);

export const CollapseIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M10 4v5H5M14 20v-5h5M4 10l6-1M20 14l-6 1" />
  </Glyph>
);

export const MoreIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="19" r="1" />
  </Glyph>
);

export const ReplyIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M9 8 4 12l5 4M4 12h9a5 5 0 0 1 5 5v1" />
  </Glyph>
);

export const EditIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="M13.5 6.5l3 3" />
  </Glyph>
);

export const TrashIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 7h16M9 7V5h6v2M6 7l1 12h10l1-12" />
  </Glyph>
);

export const DownloadIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M12 4v11M8 11l4 4 4-4M5 20h14" />
  </Glyph>
);

export const FileIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
  </Glyph>
);

export const ArrowDownIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </Glyph>
);

export const CheckIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M5 12.5 10 17 19 7" />
  </Glyph>
);

export const CheckCheckIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M2 12.5 6.5 17 15 7" />
    <path d="m12 15 1 1 8.5-9" />
  </Glyph>
);

export const RetryIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 12a8 8 0 1 1 2.3 5.6M4 20v-4h4" />
  </Glyph>
);

export const AlertIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </Glyph>
);

export const BellIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M6 9a6 6 0 0 1 12 0c0 5 1.5 6.5 2 7H4c.5-.5 2-2 2-7Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </Glyph>
);

export const MegaphoneIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 10v4a1 1 0 0 0 1 1h2l8 4V5L7 9H5a1 1 0 0 0-1 1Z" />
    <path d="M7 15v3a1.5 1.5 0 0 0 3 0v-2M18 9a3 3 0 0 1 0 6" />
  </Glyph>
);

export const ExternalLinkIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4" />
  </Glyph>
);

export const InboxIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 13l2.5-7h11L20 13v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-5Z" />
    <path d="M4 13h4l1.5 2h5L16 13h4" />
  </Glyph>
);
